import React, { useState, useMemo, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const KYC_FILTERS = [
  { id: 'all', label: 'All Buyers', color: '#1e4a72' },
  { id: 'verified', label: 'KYC Verified', color: '#059669' },
  { id: 'pending', label: 'Pending KYC', color: '#d97706' },
  { id: 'rejected_suspended', label: 'Rejected+Suspended', color: '#dc2626' },
];

const COLUMNS = [
  { key: 'name', label: 'Buyer Name', sortable: true },
  { key: 'contact', label: 'Email / Mobile', sortable: false },
  { key: 'kycStatus', label: 'KYC Status', sortable: true },
  { key: 'totalOrders', label: 'Total Orders', sortable: true },
  { key: 'totalSpent', label: 'Total Spent (₹)', sortable: true },
  { key: 'lastActive', label: 'Last Active', sortable: true },
  { key: 'registeredDate', label: 'Registered Date', sortable: true },
  { key: 'agent', label: 'Agent', sortable: false },
  { key: 'actions', label: 'Actions', sortable: false },
];

const kycBadge = (status) => {
  const map = {
    verified: { bg: '#d1fae5', color: '#065f46', label: 'KYC Verified', icon: 'CheckCircle' },
    pending: { bg: '#fef3c7', color: '#92400e', label: 'Pending KYC', icon: 'Clock' },
    rejected: { bg: '#fee2e2', color: '#991b1b', label: 'Rejected', icon: 'XCircle' },
    suspended: { bg: '#f3e8ff', color: '#6b21a8', label: 'Suspended', icon: 'AlertTriangle' },
  };
  const s = map?.[status] || map?.['pending'];
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: s?.bg, color: s?.color }}>
      <Icon name={s?.icon} size={11} />
      {s?.label}
    </span>
  );
};

const formatCurrency = (val) => {
  if (!val) return '₹0';
  if (val >= 100000) return `₹${(val / 100000)?.toFixed(1)}L`;
  if (val >= 1000) return `₹${(val / 1000)?.toFixed(0)}K`;
  return `₹${val}`;
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  if (isNaN(d?.getTime())) return dateStr;
  return d?.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const BuyersTab = () => {
  const [buyers, setBuyers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [kycFilter, setKycFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState('name');
  const [sortDir, setSortDir] = useState('asc');
  const [selectedBuyer, setSelectedBuyer] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState('');
  const [savingAgent, setSavingAgent] = useState(false);
  const [agentSaveMsg, setAgentSaveMsg] = useState('');

  const fetchBuyers = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch all buyers from user_profiles
const { data: profiles, error: fetchError } = await supabase
  ?.from('user_profiles')
  ?.select('user_id, full_name, email, mobile_phone, company_name, city, gstin, pan, kyc_status, is_active, created_at, last_login_at, referred_by_agent_id')
  ?.eq('role', 'buyer')
  ?.order('created_at', { ascending: false });

if (fetchError) throw fetchError;

// Fetch booking stats per buyer
const { data: bookings } = await supabase
  ?.from('buyer_bookings')
  ?.select('buyer_id, total_amount, status, created_at')
  ?.in('status', ['pending_approval', 'approved', 'active', 'confirmed', 'do_generated', 'completed']);  

// Group bookings by buyer_id
const bookingMap = {};
(bookings || [])?.forEach(b => {
  if (!bookingMap[b?.buyer_id]) {
    bookingMap[b?.buyer_id] = { count: 0, spent: 0, lastBookingDate: '' };
  }
  bookingMap[b?.buyer_id].count += 1;
  bookingMap[b?.buyer_id].spent += Number(b?.total_amount || 0);
  if (!bookingMap[b?.buyer_id]?.lastBookingDate || b?.created_at > bookingMap[b?.buyer_id]?.lastBookingDate) {
    bookingMap[b?.buyer_id].lastBookingDate = b?.created_at;
  }
});

const mapped = (profiles || [])?.map((u) => ({
  id: u?.user_id,
  name: u?.full_name || '—',
  email: u?.email || '',
  mobile: u?.mobile_phone || '',
  kycStatus: u?.kyc_status || (u?.gstin ? 'verified' : 'pending'),
  totalOrders: bookingMap[u?.user_id]?.count || 0,
  totalSpent: bookingMap[u?.user_id]?.spent || 0,
  lastActive: bookingMap[u?.user_id]?.lastBookingDate || u?.last_login_at || u?.created_at || '',  
  registeredDate: u?.created_at || '',
  company: u?.company_name || '',
  location: u?.city || '',
  gst: u?.gstin || '',
  pan: u?.pan || '',
  referredByAgentId: u?.referred_by_agent_id || null,
}));

setBuyers(mapped);
    } catch (err) {
      console.error('BuyersTab fetch error:', err);
      setError('Failed to load buyers. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBuyers();
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    const { data } = await supabase
      ?.from('user_profiles')
      ?.select('user_id, full_name, company_name')
      ?.eq('role', 'sales_agent')
      ?.order('full_name', { ascending: true });
    setAgents(data || []);
  };

  const getAgentName = (agentId) => {
    if (!agentId) return null;
    const agent = agents?.find(a => a?.user_id === agentId);
    return agent ? (agent?.full_name || agent?.company_name || '—') : '—';
  };

  const counts = useMemo(() => ({
    all: buyers?.length,
    verified: buyers?.filter(b => b?.kycStatus === 'verified')?.length,
    pending: buyers?.filter(b => b?.kycStatus === 'pending')?.length,
    rejected_suspended: buyers?.filter(b => b?.kycStatus === 'rejected' || b?.kycStatus === 'suspended')?.length,
  }), [buyers]);

  const filtered = useMemo(() => {
    let list = [...buyers];
    if (kycFilter === 'verified') list = list?.filter(b => b?.kycStatus === 'verified');
    else if (kycFilter === 'pending') list = list?.filter(b => b?.kycStatus === 'pending');
    else if (kycFilter === 'rejected_suspended') list = list?.filter(b => b?.kycStatus === 'rejected' || b?.kycStatus === 'suspended');

    if (search?.trim()) {
      const q = search?.toLowerCase();
      list = list?.filter(b =>
        b?.name?.toLowerCase()?.includes(q) ||
        b?.email?.toLowerCase()?.includes(q) ||
        b?.mobile?.includes(q) ||
        b?.company?.toLowerCase()?.includes(q)
      );
    }

    list?.sort((a, b) => {
      let aVal = a?.[sortKey];
      let bVal = b?.[sortKey];
      if (typeof aVal === 'string') aVal = aVal?.toLowerCase();
      if (typeof bVal === 'string') bVal = bVal?.toLowerCase();
      if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [buyers, kycFilter, search, sortKey, sortDir]);

  const handleSort = (key) => {
    if (!key) return;
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const openModal = (buyer) => {
    setSelectedBuyer(buyer);
    setSelectedAgentId(buyer?.referredByAgentId || '');
    setAgentSaveMsg('');
    setModalOpen(true);
  };
  const closeModal = () => { setModalOpen(false); setTimeout(() => setSelectedBuyer(null), 300); };

  const handleSaveAgent = async () => {
    setSavingAgent(true);
    setAgentSaveMsg('');
    const agentId = selectedAgentId || null;
    const { error } = await supabase
      ?.from('user_profiles')
      ?.update({ referred_by_agent_id: agentId })
      ?.eq('user_id', selectedBuyer?.id);
    setSavingAgent(false);
    if (error) { setAgentSaveMsg('Save failed.'); return; }
    setBuyers(prev => prev?.map(b =>
      b?.id === selectedBuyer?.id ? { ...b, referredByAgentId: agentId } : b
    ));
    setSelectedBuyer(prev => ({ ...prev, referredByAgentId: agentId }));
    setAgentSaveMsg('Saved!');
    setTimeout(() => setAgentSaveMsg(''), 2500);
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-lg font-bold" style={{ color: '#1f2937' }}>Buyer List</h2>
          <p className="text-sm mt-0.5" style={{ color: '#6b7280' }}>View and manage all registered buyers on the platform</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}>
            <Icon name="Users" size={15} />
            {buyers?.length} Total Buyers
          </div>
          <button
            onClick={fetchBuyers}
            className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-sm font-semibold border transition hover:bg-gray-50"
            style={{ borderColor: '#e5e7eb', color: '#6b7280' }}
            title="Refresh"
          >
            <Icon name="RefreshCw" size={14} />
          </button>
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-2 px-4 py-3 rounded-xl text-sm" style={{ backgroundColor: '#fee2e2', color: '#991b1b' }}>
          <Icon name="AlertCircle" size={15} />
          {error}
          <button onClick={fetchBuyers} className="ml-auto underline text-xs">Retry</button>
        </div>
      )}

      {/* KYC Filter Tabs */}
      <div className="flex flex-wrap gap-2">
        {KYC_FILTERS?.map(f => (
          <button
            key={f?.id}
            onClick={() => setKycFilter(f?.id)}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold transition-all border"
            style={kycFilter === f?.id
              ? { backgroundColor: f?.color, color: '#fff', borderColor: f?.color }
              : { backgroundColor: '#fff', color: '#4b5563', borderColor: '#e5e7eb' }
            }
          >
            {f?.label}
            <span
              className="px-1.5 py-0.5 rounded-full text-xs font-bold"
              style={kycFilter === f?.id
                ? { backgroundColor: 'rgba(255,255,255,0.25)', color: '#fff' }
                : { backgroundColor: '#f3f4f6', color: '#374151' }
              }
            >
              {counts?.[f?.id]}
            </span>
          </button>
        ))}
      </div>

      {/* Search Bar */}
      <div className="relative">
        <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none">
          <Icon name="Search" size={16} color="#9ca3af" />
        </div>
        <input
          type="text"
          value={search}
          onChange={e => setSearch(e?.target?.value)}
          placeholder="Search by name, email, mobile, or company..."
          className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-amber-400 focus:border-amber-400"
          style={{ color: '#1f2937' }}
        />
        {search && (
          <button className="absolute inset-y-0 right-3 flex items-center" onClick={() => setSearch('')}>
            <Icon name="X" size={15} color="#9ca3af" />
          </button>
        )}
      </div>

      {/* Loading */}
      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-3">
            <div className="w-8 h-8 border-2 border-amber-400 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm" style={{ color: '#9ca3af' }}>Loading buyers...</p>
          </div>
        </div>
      ) : (
        <>
          {/* Results count */}
          <p className="text-xs" style={{ color: '#9ca3af' }}>
            Showing {filtered?.length} of {buyers?.length} buyers
          </p>

          {/* Desktop Table */}
          <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr style={{ backgroundColor: '#fef3c7' }}>
                    {COLUMNS?.map(col => (
                      <th
                        key={col?.key}
                        className={`px-4 py-3 text-left font-semibold text-xs uppercase tracking-wide ${col?.sortable ? 'cursor-pointer select-none hover:bg-amber-100' : ''}`}
                        style={{ color: '#92400e' }}
                        onClick={() => col?.sortable && handleSort(col?.key)}
                      >
                        <div className="flex items-center gap-1">
                          {col?.label}
                          {col?.sortable && (
                            <span className="flex flex-col" style={{ lineHeight: 0 }}>
                              <Icon name={sortKey === col?.key && sortDir === 'asc' ? 'ChevronUp' : 'ChevronsUpDown'} size={12} color={sortKey === col?.key ? '#92400e' : '#d1a054'} />
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered?.length === 0 ? (
                    <tr>
                      <td colSpan={8} className="px-4 py-12 text-center" style={{ color: '#9ca3af' }}>
                        <div className="flex flex-col items-center gap-3">
                          <Icon name="Users" size={32} color="#d1d5db" />
                          <p className="text-sm">
                            {buyers?.length === 0
                              ? 'No approved buyers yet. Approve users from the User Approvals tab.' :'No buyers found matching your search.'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : filtered?.map((buyer, idx) => (
                    <tr
                      key={buyer?.id}
                      className="border-t hover:bg-amber-50/30 transition-colors"
                      style={{ borderColor: '#f3f4f6', backgroundColor: idx % 2 === 0 ? '#fff' : '#fffbf5' }}
                    >
                      <td className="px-4 py-3 font-medium" style={{ color: '#1f2937' }}>
                        <div className="flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ backgroundColor: '#1e4a72' }}>
                            {buyer?.name?.charAt(0)?.toUpperCase()}
                          </div>
                          <div>
                            <p className="font-semibold text-xs" style={{ color: '#1f2937' }}>{buyer?.name}</p>
                            <p className="text-xs" style={{ color: '#9ca3af' }}>{buyer?.company}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <p className="text-xs" style={{ color: '#374151' }}>{buyer?.email || '—'}</p>
                        <p className="text-xs" style={{ color: '#6b7280' }}>{buyer?.mobile || '—'}</p>
                      </td>
                      <td className="px-4 py-3">{kycBadge(buyer?.kycStatus)}</td>
                      <td className="px-4 py-3 text-center font-semibold text-sm" style={{ color: '#1f2937' }}>{buyer?.totalOrders}</td>
                      <td className="px-4 py-3 font-semibold text-sm" style={{ color: '#059669' }}>{formatCurrency(buyer?.totalSpent)}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#6b7280' }}>{formatDate(buyer?.lastActive)}</td>
                      <td className="px-4 py-3 text-xs" style={{ color: '#6b7280' }}>{formatDate(buyer?.registeredDate)}</td>
                      <td className="px-4 py-3">
                        {buyer?.referredByAgentId
                          ? <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#dbeafe', color: '#1e40af' }}><Icon name="User" size={11} />{getAgentName(buyer?.referredByAgentId)}</span>
                          : <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold" style={{ backgroundColor: '#f3f4f6', color: '#6b7280' }}>Direct</span>
                        }
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openModal(buyer)}
                          className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
                          style={{ backgroundColor: '#1e4a72' }}
                        >
                          <Icon name="Eye" size={12} />
                          View Details
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Mobile Cards */}
          <div className="md:hidden space-y-3">
            {filtered?.length === 0 ? (
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-8 text-center">
                <Icon name="Users" size={28} color="#d1d5db" />
                <p className="text-sm mt-2" style={{ color: '#9ca3af' }}>
                  {buyers?.length === 0
                    ? 'No approved buyers yet.' :'No buyers found matching your search.'}
                </p>
              </div>
            ) : filtered?.map(buyer => (
              <div key={buyer?.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <div className="w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold text-white flex-shrink-0" style={{ backgroundColor: '#1e4a72' }}>
                      {buyer?.name?.charAt(0)?.toUpperCase()}
                    </div>
                    <div>
                      <p className="font-semibold text-sm" style={{ color: '#1f2937' }}>{buyer?.name}</p>
                      <p className="text-xs" style={{ color: '#9ca3af' }}>{buyer?.company}</p>
                    </div>
                  </div>
                  {kycBadge(buyer?.kycStatus)}
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs mb-3" style={{ color: '#6b7280' }}>
                  <div><span className="font-medium" style={{ color: '#374151' }}>Mobile:</span> {buyer?.mobile || '—'}</div>
                  <div><span className="font-medium" style={{ color: '#374151' }}>Orders:</span> {buyer?.totalOrders}</div>
                  <div><span className="font-medium" style={{ color: '#374151' }}>Spent:</span> <span style={{ color: '#059669', fontWeight: 600 }}>{formatCurrency(buyer?.totalSpent)}</span></div>
                  <div><span className="font-medium" style={{ color: '#374151' }}>Last Active:</span> {formatDate(buyer?.lastActive)}</div>
                  <div className="col-span-2"><span className="font-medium" style={{ color: '#374151' }}>Agent:</span> {buyer?.referredByAgentId ? getAgentName(buyer?.referredByAgentId) : <span style={{ color: '#9ca3af' }}>Direct</span>}</div>
                </div>
                <button
                  onClick={() => openModal(buyer)}
                  className="w-full flex items-center justify-center gap-2 py-2 rounded-lg text-xs font-semibold text-white transition hover:opacity-90"
                  style={{ backgroundColor: '#1e4a72' }}
                >
                  <Icon name="Eye" size={13} />
                  View Details
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      {/* View Details Slide-Up Modal */}
      {/* Backdrop */}
      <div
        className={`fixed inset-0 z-40 transition-opacity duration-300 ${modalOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'}`}
        style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
        onClick={closeModal}
      />
      {/* Slide-up panel */}
      <div
        className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ease-out ${modalOpen ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ maxHeight: '90vh', overflowY: 'auto' }}
      >
        {selectedBuyer && (
          <div className="rounded-t-3xl shadow-2xl" style={{ backgroundColor: '#0f172a' }}>
            {/* Handle */}
            <div className="flex justify-center pt-3 pb-1">
              <div className="w-10 h-1 rounded-full" style={{ backgroundColor: '#334155' }} />
            </div>
            {/* Modal Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b" style={{ borderColor: '#1e293b' }}>
              <div className="flex items-center gap-3">
                <div className="w-11 h-11 rounded-full flex items-center justify-center text-base font-bold text-white" style={{ backgroundColor: '#1e4a72' }}>
                  {selectedBuyer?.name?.charAt(0)?.toUpperCase()}
                </div>
                <div>
                  <h3 className="font-bold text-base text-white">{selectedBuyer?.name}</h3>
                  <p className="text-xs" style={{ color: '#94a3b8' }}>{selectedBuyer?.company} · {selectedBuyer?.location}</p>
                </div>
              </div>
              <button onClick={closeModal} className="w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-slate-700" style={{ backgroundColor: '#1e293b' }}>
                <Icon name="X" size={16} color="#94a3b8" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="px-6 py-5 space-y-5">
              {/* KYC Status */}
              <div className="flex items-center justify-between p-4 rounded-xl" style={{ backgroundColor: '#1e293b' }}>
                <span className="text-sm font-medium" style={{ color: '#94a3b8' }}>KYC Status</span>
                {kycBadge(selectedBuyer?.kycStatus)}
              </div>

              {/* Stats Row */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { label: 'Total Orders', value: selectedBuyer?.totalOrders, icon: 'ShoppingCart', color: '#3b82f6' },
                  { label: 'Total Spent', value: formatCurrency(selectedBuyer?.totalSpent), icon: 'IndianRupee', color: '#10b981' },
                  { label: 'Last Active', value: formatDate(selectedBuyer?.lastActive), icon: 'Activity', color: '#f59e0b' },
                ]?.map(stat => (
                  <div key={stat?.label} className="p-3 rounded-xl text-center" style={{ backgroundColor: '#1e293b' }}>
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center mx-auto mb-2" style={{ backgroundColor: `${stat?.color}22` }}>
                      <Icon name={stat?.icon} size={16} color={stat?.color} />
                    </div>
                    <p className="text-xs font-bold text-white">{stat?.value}</p>
                    <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>{stat?.label}</p>
                  </div>
                ))}
              </div>

              {/* Contact Info */}
              <div className="p-4 rounded-xl space-y-3" style={{ backgroundColor: '#1e293b' }}>
                <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748b' }}>Contact Information</h4>
                <div className="space-y-2">
                  {[
                    { icon: 'Mail', label: 'Email', value: selectedBuyer?.email },
                    { icon: 'Phone', label: 'Mobile', value: selectedBuyer?.mobile },
                    { icon: 'MapPin', label: 'Location', value: selectedBuyer?.location },
                  ]?.map(item => (
                    <div key={item?.label} className="flex items-center gap-3">
                      <div className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#0f172a' }}>
                        <Icon name={item?.icon} size={13} color="#94a3b8" />
                      </div>
                      <div>
                        <p className="text-xs" style={{ color: '#64748b' }}>{item?.label}</p>
                        <p className="text-sm font-medium text-white">{item?.value || '—'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Registration Info */}
              <div className="p-4 rounded-xl space-y-3" style={{ backgroundColor: '#1e293b' }}>
                <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748b' }}>Registration Details</h4>
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { label: 'Registered On', value: formatDate(selectedBuyer?.registeredDate) },
                    { label: 'GST Number', value: selectedBuyer?.gst || 'Not Provided' },
                  ]?.map(item => (
                    <div key={item?.label} className="p-3 rounded-lg" style={{ backgroundColor: '#0f172a' }}>
                      <p className="text-xs mb-1" style={{ color: '#64748b' }}>{item?.label}</p>
                      <p className="text-sm font-semibold text-white">{item?.value}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Agent Assignment */}
              <div className="p-4 rounded-xl space-y-3" style={{ backgroundColor: '#1e293b' }}>
                <h4 className="text-xs font-semibold uppercase tracking-wide" style={{ color: '#64748b' }}>Agent Assignment</h4>
                <select
                  value={selectedAgentId}
                  onChange={e => { setSelectedAgentId(e.target.value); setAgentSaveMsg(''); }}
                  className="w-full rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  style={{ backgroundColor: '#0f172a', color: '#f1f5f9', border: '1px solid #334155' }}
                >
                  <option value="">Direct (no agent)</option>
                  {agents?.map(a => (
                    <option key={a?.user_id} value={a?.user_id}>
                      {a?.full_name || a?.company_name || a?.user_id}
                    </option>
                  ))}
                </select>
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleSaveAgent}
                    disabled={savingAgent}
                    className="flex-1 py-2 rounded-lg text-sm font-semibold transition hover:opacity-90 disabled:opacity-50"
                    style={{ backgroundColor: '#1e4a72', color: '#fff' }}
                  >
                    {savingAgent ? 'Saving…' : 'Save Assignment'}
                  </button>
                  {agentSaveMsg && (
                    <span className="text-xs font-semibold" style={{ color: agentSaveMsg === 'Saved!' ? '#10b981' : '#ef4444' }}>
                      {agentSaveMsg}
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="px-6 pb-8 pt-2">
              <button
                onClick={closeModal}
                className="w-full py-3 rounded-xl text-sm font-semibold transition hover:opacity-90"
                style={{ backgroundColor: '#1e4a72', color: '#fff' }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BuyersTab;

